import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2 } from "lucide-react";
import { Reveal, SectionHeading } from "../components/primitives";
import { PageHero, CTASection, Icon, BookButtons } from "../components/sections";
import { SERVICES } from "../data/site";

const Services = () => (
  <>
    <PageHero
      eyebrow="DWC Services"
      lines={["Everything You Need To", "Become A Confident Driver."]}
      text="Driving training, confidence training, driving psychology, RTO guidance and more — all under one structured system in Mumbai, Surat & Ahmedabad."
    >
      <BookButtons bookTestId="services-book-btn" waTestId="services-whatsapp-btn" />
    </PageHero>
    <section className="bg-white py-24 md:py-32" data-testid="services-grid-section">
      <div className="mx-auto max-w-7xl px-5 lg:px-8">
        <SectionHeading
          eyebrow="Our Services"
          number="01"
          title="Choose The Service That Fits You"
          text="Every service follows the same DWC promise — structured training, personal attention and real road practice."
        />
        <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {SERVICES.map((s, i) => (
            <Reveal key={s.title} delay={(i % 4) * 0.08} className="h-full">
              <Link
                to={s.to}
                data-testid={`service-card-${i}`}
                className="group flex h-full flex-col rounded-2xl border border-gray-100 bg-white p-7 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-[box-shadow,transform,border-color] duration-300 hover:-translate-y-1 hover:border-dwc-green/30 hover:shadow-[0_20px_50px_rgb(23,107,58,0.10)]"
              >
                <span className="inline-flex h-12 w-12 items-center justify-center rounded-xl bg-dwc-soft text-dwc-green transition-colors duration-300 group-hover:bg-dwc-green group-hover:text-white">
                  <Icon name={s.icon} />
                </span>
                <h2 className="mt-5 text-base font-extrabold tracking-tight text-dwc-charcoal">{s.title}</h2>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-dwc-charcoal/65">{s.text}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-extrabold uppercase tracking-wide text-dwc-green">
                  Learn More
                  <ArrowRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-1" aria-hidden="true" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
    <CTASection title="Not Sure Which Service You Need?" sub="Book an appointment — the DWC team will understand your situation and suggest the right starting point." chapter="02" />
  </>
);

export default Services;

const GuidancePage = ({ eyebrow, lines, heroText, sections, disclaimer, testId }) => (
  <>
    <PageHero eyebrow={eyebrow} lines={lines} text={heroText}>
      <BookButtons bookTestId={`${testId}-book-btn`} waTestId={`${testId}-whatsapp-btn`} />
      {testId === "learning-licence" && (
        <Link to="/learning-licence-prep" data-testid="start-preparation-btn" className="inline-flex items-center justify-center rounded-xl bg-dwc-crimson px-7 py-4 text-sm font-bold uppercase tracking-wide text-white transition-transform duration-300 hover:-translate-y-0.5">
          Start Preparation
        </Link>
      )}
    </PageHero>
    <section className="bg-white py-24 md:py-32" data-testid={`${testId}-content`}>
      <div className="mx-auto grid max-w-7xl gap-12 px-5 lg:grid-cols-2 lg:px-8">
        {sections.map((sec, si) => (
          <div key={sec.title}>
            <SectionHeading eyebrow={sec.eyebrow} number={String(si + 1).padStart(2, "0")} title={sec.title} text={sec.text} />
            <ul className="mt-8 space-y-3">
              {sec.items.map((it) => (
                <li key={it} className="flex items-start gap-3 rounded-xl border border-dwc-green/15 bg-dwc-soft p-4 text-sm font-semibold text-dwc-charcoal/80">
                  <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-dwc-green" aria-hidden="true" />
                  {it}
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
      {disclaimer && (
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <Reveal delay={0.15}>
            <p className="mt-14 rounded-2xl border border-dwc-green/15 bg-dwc-soft p-6 text-sm leading-relaxed text-dwc-charcoal/70" data-testid={`${testId}-disclaimer`}>
              <strong className="text-dwc-green">Please note:</strong> {disclaimer}
            </p>
          </Reveal>
        </div>
      )}
    </section>
    <CTASection title="Need Guidance? Talk To DWC." sub="Message us on WhatsApp with what you need — the team will guide you through the process." chapter="03" />
  </>
);

export const LearningLicence = () => (
  <GuidancePage
    testId="learning-licence"
    eyebrow="Learning Licence Guidance"
    lines={["Your Learning Licence,", "Guided Step By Step."]}
    heroText="DWC guides you through the learning licence process — documents, application and preparation — so you can start your driving journey without confusion."
    sections={[
      {
        eyebrow: "What We Guide You On",
        title: "Learning Licence Guidance",
        text: "Clear, simple guidance for every step of your learning licence.",
        items: ["Understanding eligibility and required documents", "Application guidance", "Learning licence test preparation guidance", "What to expect on the test day", "Next steps after your learning licence"],
      },
      {
        eyebrow: "After Your Learning Licence",
        title: "From Licence To Confidence",
        text: "A learning licence is just the beginning. DWC helps you use it well.",
        items: ["Structured driving training under the 33-Lesson System", "Real manual driving practice for the RTO test", "Guidance for your permanent driving licence", "Lifetime free RTO consulting within the scope of the service"],
      },
    ]}
    disclaimer="DWC provides guidance and consulting for licence-related processes. DWC is a private driving institute and not the Regional Transport Office or any government authority. Licences are issued solely by the government RTO, and government fees apply as per law."
  />
);

export const ManualPractice = () => (
  <GuidancePage
    testId="manual-practice"
    eyebrow="Manual Driving Practice for RTO"
    lines={["Real Manual Practice", "For Your RTO Test."]}
    heroText="Practise real manual driving — clutch, gears and control — so you walk into your RTO driving test prepared and calm."
    sections={[
      {
        eyebrow: "What You Practise",
        title: "Real Manual Driving Practice",
        text: "Real car, real roads, real test-style practice.",
        items: ["Clutch and gear control", "Smooth starting and stopping", "Slope starts and slow-speed control", "Reverse driving practice", "Test-pattern practice for the RTO driving test"],
      },
      {
        eyebrow: "Why It Matters",
        title: "Prepared, Not Nervous",
        text: "Most test failures come from nervousness and lack of real manual practice — not lack of ability.",
        items: ["Practice reduces test-day fear", "Trainer feedback on your weak areas", "Confidence with the exact skills the test checks", "Continued training options after your test"],
      },
    ]}
    disclaimer="DWC provides driving practice and preparation. The driving test is conducted and evaluated solely by the government RTO. DWC does not guarantee government test outcomes."
  />
);
